import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  CallPrepItem,
  CallPrepSource,
  ModalViewMode,
  RightPanelMode,
} from '../types';

/** Shape of data persisted to localStorage */
interface PersistedNotes {
  callPrepItems: CallPrepItem[];
  customNote: string;
}

const STORAGE_KEY_PREFIX = 'initial-review-notes-';

/** Get localStorage key for a matter */
const getStorageKey = (matterId: string) => `${STORAGE_KEY_PREFIX}${matterId}`;

/** Safely load persisted notes from localStorage with runtime validation */
const loadPersistedNotes = (matterId: string): PersistedNotes | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(matterId));
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);

    // Runtime validation to ensure data matches expected shape
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'callPrepItems' in parsed &&
      'customNote' in parsed &&
      Array.isArray((parsed as PersistedNotes).callPrepItems) &&
      typeof (parsed as PersistedNotes).customNote === 'string'
    ) {
      return parsed as PersistedNotes;
    }

    // Data doesn't match expected shape - clear corrupted data
    localStorage.removeItem(getStorageKey(matterId));
    return null;
  } catch {
    return null;
  }
};

/** Save notes to localStorage */
const saveNotesToStorage = (matterId: string, notes: PersistedNotes) => {
  try {
    localStorage.setItem(getStorageKey(matterId), JSON.stringify(notes));
  } catch {
    // Storage full or unavailable - fail silently
  }
};

/** Clear persisted notes from localStorage */
const clearPersistedNotesFromStorage = (matterId: string) => {
  try {
    localStorage.removeItem(getStorageKey(matterId));
  } catch {
    // Fail silently
  }
};

interface UseInitialReviewStateOptions {
  matterId: string;
}

/**
 * Manages local state for the Initial Review modal.
 * - Call prep items (auto-populated and user-added)
 * - Right panel mode (call prep vs document preview)
 * - Modal view mode (main review vs full document view)
 * - Persists notes to localStorage for draft recovery
 */
export const useInitialReviewState = ({
  matterId,
}: UseInitialReviewStateOptions) => {
  // Load persisted notes on mount
  const persistedNotes = useMemo(
    () => loadPersistedNotes(matterId),
    [matterId],
  );

  // Call prep items state
  const [callPrepItems, setCallPrepItems] = useState<CallPrepItem[]>(
    () => persistedNotes?.callPrepItems ?? [],
  );

  // Right panel mode state
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>({
    mode: 'callPrep',
  });

  // Modal view mode state
  const [modalViewMode, setModalViewMode] = useState<ModalViewMode>({
    mode: 'review',
  });

  // User-added custom note
  const [customNote, setCustomNote] = useState(
    () => persistedNotes?.customNote ?? '',
  );

  // Document library open state (persists across view mode changes)
  const [isDocumentLibraryOpen, setIsDocumentLibraryOpen] = useState(false);

  // Debounced persistence to localStorage
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 500ms
    saveTimeoutRef.current = setTimeout(() => {
      saveNotesToStorage(matterId, { callPrepItems, customNote });
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [matterId, callPrepItems, customNote]);

  // Clear persisted notes (call on successful completion)
  const clearPersistedNotes = useCallback(() => {
    clearPersistedNotesFromStorage(matterId);
  }, [matterId]);

  // Generate unique ID for call prep items
  const generateId = useCallback(
    () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  // Add item to call prep
  const addCallPrepItem = useCallback(
    (text: string, source: CallPrepSource, sourceId?: string) => {
      const newItem: CallPrepItem = {
        id: generateId(),
        text,
        source,
        sourceId,
      };
      setCallPrepItems((prev) => [...prev, newItem]);
      return newItem.id;
    },
    [generateId],
  );

  // Remove item from call prep
  const removeCallPrepItem = useCallback((id: string) => {
    setCallPrepItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Remove item by source ID (for toggling off)
  const removeCallPrepItemBySourceId = useCallback((sourceId: string) => {
    setCallPrepItems((prev) =>
      prev.filter((item) => item.sourceId !== sourceId),
    );
  }, []);

  // Check if a source ID is in call prep
  const isInCallPrep = useCallback(
    (sourceId: string) => {
      return callPrepItems.some((item) => item.sourceId === sourceId);
    },
    [callPrepItems],
  );

  // Toggle a context field in call prep
  const toggleContextInCallPrep = useCallback(
    (sourceId: string, text: string) => {
      if (isInCallPrep(sourceId)) {
        removeCallPrepItemBySourceId(sourceId);
      } else {
        addCallPrepItem(text, 'context', sourceId);
      }
    },
    [isInCallPrep, removeCallPrepItemBySourceId, addCallPrepItem],
  );

  // Add document to collect to call prep
  const addDocumentToCollect = useCallback(
    (documentName: string, deliverableId: string) => {
      const text = `Collect: ${documentName}`;
      addCallPrepItem(text, 'document', `doc_${deliverableId}`);
    },
    [addCallPrepItem],
  );

  // Remove document from call prep
  const removeDocumentFromCallPrep = useCallback(
    (deliverableId: string) => {
      removeCallPrepItemBySourceId(`doc_${deliverableId}`);
    },
    [removeCallPrepItemBySourceId],
  );

  // Check if document is in call prep
  const isDocumentInCallPrep = useCallback(
    (deliverableId: string) => isInCallPrep(`doc_${deliverableId}`),
    [isInCallPrep],
  );

  // Show document preview
  const showDocumentPreview = useCallback((artifactId: string) => {
    setRightPanelMode({ mode: 'document', artifactId });
  }, []);

  // Show call prep panel
  const showCallPrep = useCallback(() => {
    setRightPanelMode({ mode: 'callPrep' });
  }, []);

  // Show full modal document view
  const showFullDocumentView = useCallback((artifactId: string) => {
    setModalViewMode({ mode: 'document', artifactId });
  }, []);

  // Show full modal task view
  const showTaskView = useCallback((taskId: string) => {
    setModalViewMode({ mode: 'task', taskId });
  }, []);

  // Show main review view
  const showReviewView = useCallback(() => {
    setModalViewMode({ mode: 'review' });
  }, []);

  // Get formatted call prep text for saving (markdown format)
  const getFormattedCallPrep = useCallback(() => {
    const lines: string[] = [];

    // Group by source
    const contextItems = callPrepItems.filter(
      (item) => item.source === 'context',
    );
    const documentItems = callPrepItems.filter(
      (item) => item.source === 'document',
    );

    if (contextItems.length > 0) {
      lines.push('**Discussion Points:**');
      contextItems.forEach((item) => lines.push(`- ${item.text}`));
      lines.push('');
    }

    if (documentItems.length > 0) {
      lines.push('**Documents to Collect:**');
      documentItems.forEach((item) => lines.push(`- ${item.text}`));
      lines.push('');
    }

    if (customNote.trim()) {
      lines.push('**Additional Notes:**');
      lines.push(customNote.trim());
    }

    return lines.join('\n');
  }, [callPrepItems, customNote]);

  // Check if there are any items in call prep
  const hasCallPrepItems = useMemo(
    () => callPrepItems.length > 0 || customNote.trim().length > 0,
    [callPrepItems.length, customNote],
  );

  return {
    // Call prep
    callPrepItems,
    addCallPrepItem,
    removeCallPrepItem,
    toggleContextInCallPrep,
    isInCallPrep,
    getFormattedCallPrep,
    hasCallPrepItems,
    customNote,
    setCustomNote,

    // Documents
    addDocumentToCollect,
    removeDocumentFromCallPrep,
    isDocumentInCallPrep,

    // Right panel
    rightPanelMode,
    showDocumentPreview,
    showCallPrep,

    // Modal view
    modalViewMode,
    showFullDocumentView,
    showTaskView,
    showReviewView,

    // Document library
    isDocumentLibraryOpen,
    setIsDocumentLibraryOpen,

    // Persistence
    clearPersistedNotes,
  };
};

export type InitialReviewState = ReturnType<typeof useInitialReviewState>;
